import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex, Text } from "styles/utility-components";
import z from "zod";

import Button from "components/ui/button/Button";
import Drawer from "components/ui/drawer/Drawer";
import Input from "components/ui/input/Input";
import Label from "components/ui/input/Label";
import TextAreaInput from "components/ui/input/TextAreaInput";
import toast from "components/ui/toast/Toast";
import {
  useCreateAiQuickPrompt,
  useUpdateAiQuickPrompt,
} from "lib/hooks/api/ai_quick_prompts/useAiQuickPromptMutations";
import { AiQuickPrompt } from "lib/hooks/api/ai_quick_prompts/useAiQuickPrompts";
import { EventNames, useTracking } from "lib/tracking";
import { AiQuickPromptChatType } from "lib/types/graphQLEnums";

// Match the backend's limits
const MIN_QUICK_PROMPT_LENGTH = 10;
const MAX_QUICK_PROMPT_LENGTH = 2000;
const MAX_QUICK_PROMPT_NAME_LENGTH = 60;
// Roughly what fits on one line of the assistant's menu
const LONG_PROMPT_LENGTH = 70;

const quickPromptSchema = z.object({
  prompt: z
    .string()
    .trim()
    .min(MIN_QUICK_PROMPT_LENGTH, `Add at least ${MIN_QUICK_PROMPT_LENGTH} characters`)
    .max(
      MAX_QUICK_PROMPT_LENGTH,
      `Keep it to ${MAX_QUICK_PROMPT_LENGTH.toLocaleString()} characters or fewer`,
    ),
  name: z.string().trim().max(MAX_QUICK_PROMPT_NAME_LENGTH),
});

type QuickPromptFormInput = z.input<typeof quickPromptSchema>;
type QuickPromptFormOutput = z.output<typeof quickPromptSchema>;

type Props = {
  chatType: AiQuickPromptChatType;
  open: boolean;
  onClose: () => void;
  // The prompt to edit; without one, the drawer adds a prompt
  quickPrompt?: AiQuickPrompt;
  // Whether the team had already changed its list, for tracking
  isCustomised: boolean;
};

/**
 * Adds or edits one of the team's quick prompts, and closes once the change has saved so a failed
 * save keeps what was typed. Starts from the prompt it was opened with, so it needs a new `key`
 * each time it opens.
 */
export default function AddOrEditQuickPromptDrawer({
  chatType,
  open,
  onClose,
  quickPrompt,
  isCustomised,
}: Props): JSX.Element {
  const { logEvent } = useTracking();
  const { control, formState, handleSubmit, watch } = useForm<
    QuickPromptFormInput,
    unknown,
    QuickPromptFormOutput
  >({
    mode: "onChange",
    defaultValues: { prompt: quickPrompt?.prompt ?? "", name: quickPrompt?.name ?? "" },
    resolver: zodResolver(quickPromptSchema),
  });

  const onError = () => toast.error("Unable to update your quick prompts; please try again");
  const { mutate: createPrompt, isLoading: isCreating } = useCreateAiQuickPrompt({
    onSuccess: (_data, { input }) => {
      toast.success("Quick prompts updated");
      logEvent(EventNames.quickPromptCreated, {
        "Has name": Boolean(input.name),
        "First customisation": !isCustomised,
      });
      onClose();
    },
    onError,
  });
  const { mutate: updatePrompt, isLoading: isUpdating } = useUpdateAiQuickPrompt({
    onSuccess: (_data, { input }) => {
      toast.success("Quick prompts updated");
      logEvent(EventNames.quickPromptEdited, { "Has name": Boolean(input.name) });
      onClose();
    },
    onError,
  });
  const isSaving = isCreating || isUpdating;

  const onSubmit = ({ prompt, name }: QuickPromptFormOutput) => {
    const input = { chatType, prompt, name: name || null };
    if (quickPrompt) {
      updatePrompt({ input: { ...input, id: quickPrompt.id } });
    } else {
      createPrompt({ input });
    }
  };

  const suggestName = !watch("name").trim() && watch("prompt").trim().length > LONG_PROMPT_LENGTH;

  return (
    // A design system drawer, so it can open over the assistant's quick prompts drawer
    <Drawer open={open} onOpenChange={(isOpen) => !isOpen && onClose()} width={440} padding={24}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Flex column gap={24}>
          <Flex column gap={8}>
            <Flex justifyContent="space-between" alignItems="center">
              <Text h3>{quickPrompt ? "Edit" : "Add"} quick prompt</Text>
              <Button
                icon="xClose"
                buttonType="tertiary"
                aria-label="Close"
                onClick={onClose}
                disabled={isSaving}
              />
            </Flex>
            <Text description color="sysTextSecondary">
              Choosing a quick prompt in the AI assistant puts this text in the input, ready to edit
              and send.
            </Text>
          </Flex>
          <div>
            <Label htmlFor="quick-prompt-text">Prompt</Label>
            <TextAreaInput
              id="quick-prompt-text"
              name="prompt"
              control={control}
              minRows={4}
              autoFocus
              placeholder="e.g. Draft a full response in 500 words using our content library"
            />
            {formState.errors.prompt && (
              <Text description color="sysDestructiveDefault">
                {formState.errors.prompt.message}
              </Text>
            )}
          </div>
          <div>
            <Label htmlFor="quick-prompt-name">Short name (optional)</Label>
            <Input
              id="quick-prompt-name"
              name="name"
              control={control}
              maxLength={MAX_QUICK_PROMPT_NAME_LENGTH}
              placeholder="e.g. Full draft"
            />
            <Text description color="sysTextSecondary">
              {suggestName
                ? "Long prompts are cut off in the menu. Add a short name to show there instead."
                : "Shown in the menu instead of the full prompt."}
            </Text>
          </div>
        </Flex>
        <Flex gap={8} alignItems="center" justifyContent="flex-end">
          <Button buttonType="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!formState.isValid || !formState.isDirty || isSaving}
            loading={isSaving}
          >
            Save
          </Button>
        </Flex>
      </Form>
    </Drawer>
  );
}

const Form = styled.form({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  height: "100%",
  gap: 24,
});
